import { usePermissions } from "@/providers/permissions-provider";
import { PermissionKey } from "@/types/permissions";

/**
 * Hook to check if the current user has specific permissions
 * @param permission - Single permission or array of permissions to check
 * @param requireAll - If true, requires all permissions. If false, requires any permission (default: false)
 * @returns boolean indicating if user has the required permission(s)
 */
export function usePermission(
  permission: PermissionKey | PermissionKey[],
  requireAll: boolean = false
): boolean {
  const { hasPermission, hasAnyPermission, hasAllPermissions } = usePermissions();

  if (Array.isArray(permission)) {
    return requireAll ? hasAllPermissions(permission) : hasAnyPermission(permission);
  }

  return hasPermission(permission);
}

/**
 * Hook to get permission checking functions
 * Useful when you need to check permissions dynamically
 */
export function usePermissionCheck() {
  const { hasPermission, hasAnyPermission, hasAllPermissions } = usePermissions();

  return {
    hasPermission,
    hasAnyPermission,
    hasAllPermissions,
  };
}
